import { useMemo } from 'react';

import { useCurrentGame } from '@/features/games/context/CurrentGameContext';

/**
 * Pixel render-scale for the playing-surface visualization (rink/court).
 *
 * This scales only the RENDERED size of the surface — never the coordinate
 * basis (`surfaceWidth`/`surfaceHeight`), which must stay equal to the physical
 * surface so tracking positions normalize correctly. Resizing via the DB
 * dimensions instead would shrink entities toward the center, because they're
 * normalized against that basis.
 *
 * `BASE_SURFACE_SCALE` is the historical factor applied to every surface.
 * `LEAGUE_SCALE_MULTIPLIER` is the per-league knob layered on top — e.g. NBA
 * courts render 2× larger than the base so they fill the narrow auto-iso panel.
 * Bump the NBA value to make the court bigger/smaller.
 *
 * Consumed by BOTH GamePositionsRepresentation (surface width) and
 * useGameTypeMetadataHeight (derives height from that width) — keep it the
 * single source of truth so width and height never drift apart.
 */
const BASE_SURFACE_SCALE = 1.9;

const LEAGUE_SCALE_MULTIPLIER: Record<string, number> = {
  NBA: 2,
};

const useSurfaceRenderScale = (): number => {
  const { game } = useCurrentGame();
  const leagueName = game?.season?.league?.name;
  return useMemo(() => {
    const league = leagueName?.toUpperCase() ?? '';
    const SURFACE_RENDER_SCALE=  BASE_SURFACE_SCALE * (LEAGUE_SCALE_MULTIPLIER[league] ?? 1);
    return SURFACE_RENDER_SCALE;
  }, [leagueName]);
};

export default useSurfaceRenderScale;
